import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  effect,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  QueryList,
  SimpleChanges,
  ViewChild,
  viewChildren,
  ViewChildren,
} from '@angular/core';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { MatDialog } from '@angular/material/dialog';
import { auditTime, finalize, firstValueFrom, forkJoin, map, Subject } from 'rxjs';
import { Ambito } from 'src/app/services/ambiti/ambiti.service';
import { AziendaService } from 'src/app/services/azienda/azienda.service';
import {
  CompilaTematiche,
  ReportVsmeService,
} from 'src/app/services/report-vsme/report-vsme.service';
import { OpzioneTematica, Tematica } from 'src/app/services/tematiche/tematiche.service';
import {
  UnitaDiMisura,
  UnitaDiMisuraService,
} from 'src/app/services/unita-di-misura/unita-di-misura.service';
import { UtenteIncaricatoService } from 'src/app/services/utente/utente-incaricato.service';
import { TipoElemento, UtilityService } from 'src/app/services/utility/utility.service';
import { SwiperContainer } from 'swiper/element';
import { ElementoForAI } from '../app-follia-widget/chat-data-interface';
import { Filters } from '../lista-tabellare/classes/lista-tabellare-data-source';
import {
  BottomSheetInfoElementiComponent,
  InfoElemento,
} from '../selezione-elementi-questionario/bottom-sheet-info-elementi/bottom-sheet-info-elementi.component';
import { DialogAssociaFiguraAziendaleComponent } from '../selezione-elementi-questionario/dialog/dialog-associa-figura-aziendale/dialog-associa-figura-aziendale.component';
import { SpinnerOverlayComponent } from '../spinner-overlay/spinner-overlay.component';
import { TabellaDinamica } from '../tabella-cfg-dinamica/tabella-cfg-dinamica.component';

// ─── Tipi interni ────────────────────────────────────────────────────────────

/** Risultato della ricerca della prima tematica senza risposte complete. */
interface TematicaTarget {
  /** Indice dell'ambito contenente la tematica incompleta (undefined = tutte complete). */
  indiceAmbito: number | undefined;
  /** Indice della tematica incompleta all'interno dell'ambito (undefined = tutte complete). */
  indiceTematica: number | undefined;
  /** Indice globale della slide corrispondente. */
  currentSlideIndex: number;
}

/** Codici di pre-compilazione supportati per i tooltip. */
type CodicePreCompilazione = 'PA' | 'SA' | 'AA' | 'BA' | 'BANN' | 'SANN' | 'AANN';

// ─── Costanti ─────────────────────────────────────────────────────────────────

/** Durata in ms delle snack bar di errore brevi. */
const SNACK_DURATION_SHORT = 2000;
/** Durata in ms delle snack bar di errore lunghe. */
const SNACK_DURATION_LONG = 8000;
/** Throttle in ms per l'aggiornamento dell'altezza degli Swiper. */
const SWIPER_RESIZE_AUDIT_MS = 32;
/** Delay in ms per l'aggiornamento sincrono degli Swiper. */
const SWIPER_UPDATE_DELAY_MS = 100;

@Component({
  selector: 'app-compila-report-vsme',
  standalone: false,
  templateUrl: './compila-report-vsme.component.html',
  styleUrl: './compila-report-vsme.component.scss',
})
export class CompilaReportVsmeComponent implements AfterViewInit, OnChanges, OnDestroy {
  // ─── Output ───────────────────────────────────────────────────────────────

  /**
   * Emette `true` quando la compilazione del report è completata
   * (ultima slide salvata o navigazione verso l'esterno).
   */
  @Output() readonly completato = new EventEmitter<boolean>();

  // ─── ViewChild / ViewChildren ─────────────────────────────────────────────

  /** Spinner di overlay mostrato durante le operazioni asincrone. */
  @ViewChild(SpinnerOverlayComponent) spinnerOver!: SpinnerOverlayComponent;

  /** Swiper padre che contiene gli ambiti. */
  @ViewChild('swiperAmbiti') swiperAmbiti!: ElementRef<SwiperContainer>;

  /** Lista degli Swiper figli, uno per ambito, ciascuno contenente le tematiche. */
  @ViewChildren('swiperTematiche') swiperTematiche!: QueryList<ElementRef<SwiperContainer>>;

  /**
   * Lista reattiva (Signal) di tutte le `<textarea>` con riferimento `#textArea`.
   * Usata dal ResizeObserver per aggiornare l'altezza degli Swiper.
   */
  private readonly textAreas = viewChildren<ElementRef<HTMLTextAreaElement>>('textArea');

  // ─── Stato UI (Signals) ───────────────────────────────────────────────────

  /** Indice globale della slide corrente (somma di tutte le tematiche precedenti). */
  public currentSlideIndex = 0;

  /** Numero totale di tematiche su tutti gli ambiti. */
  public totTematiche = 0;

  /** Testo del pulsante di avanzamento. */
  public titoloBtnAvanti = 'Avanti';

  /** Indice dell'ambito attualmente visualizzato. */
  public indexAmbitoCorrente = 0;

  /** Indice della tematica corrente all'interno dell'ambito visualizzato. */
  public indexTematicaCorrente = 0;

  /**
   * Mappa `{ idAmbito: punteggioPercentuale }` aggiornata in tempo reale
   * durante la compilazione per visualizzare il punteggio per ambito.
   */
  public mappaPunteggioAmbiti: Record<string, number> = {};

  /**
   * Mappa `{ idCategoriaUnitaDiMisura: UnitaDiMisura[] }` popolata lazy
   * al momento del caricamento dei KPI presenti nella compilazione.
   */
  public arrayUm: Record<string, UnitaDiMisura[]> = {};

  /** Stringa che descrive la figura referente dell'azienda. */
  public referente = '';

  // ─── Input ────────────────────────────────────────────────────────────────

  private _compilazione: CompilaTematiche | undefined;

  /**
   * Oggetto principale della compilazione VSME, contenente ambiti, tematiche
   * e metadati del report. Al cambio del valore vengono aggiornati i contatori
   * e, se il modulo è di tipo PMI, eseguita la pre-selezione iniziale.
   */
  @Input()
  set compilazione(value: CompilaTematiche | undefined) {
    this._compilazione = value;
    this._ricalcolaTotTematiche();

    if (this.isPMI) {
      this.mapPrecheckIniziale();
    }

    this._updateSwiperHeight();
  }

  get compilazione(): CompilaTematiche | undefined {
    return this._compilazione;
  }

  private _idModulo!: string;

  /** Identificativo del modulo VSME corrente, usato nelle chiamate API. */
  @Input()
  set idModulo(value: string) {
    this._idModulo = value;
  }

  get idModulo(): string {
    return this._idModulo;
  }

  private _readOnly = false;

  /**
   * Se `true`, la compilazione è in sola lettura: i controlli di validazione
   * vengono bypassati e la navigazione tra slide è libera.
   */
  @Input()
  set readOnly(value: boolean) {
    this._readOnly = value;
  }

  get readOnly(): boolean {
    return this._readOnly;
  }

  private _temporaryToken: string | undefined;

  /**
   * Token temporaneo opzionale, usato per autenticare le chiamate API
   * in contesti come la compilazione da link esterno (es. utente non loggato).
   */
  @Input()
  set temporaryToken(value: string | undefined) {
    this._temporaryToken = value;
  }

  get temporaryToken(): string | undefined {
    return this._temporaryToken;
  }

  // ─── Getters derivati ─────────────────────────────────────────────────────

  /** Lista degli ambiti della compilazione corrente. */
  get ambiti(): Ambito[] {
    return this._compilazione?.ambiti ?? [];
  }

  /** `true` se l'utente ha attivato l'integrazione con l'IA. */
  get isIntegrazioneAI(): boolean {
    return this.utenteIncaricatoService.isIntegrazioneIA();
  }

  /** `true` se l'azienda è classificata come PMI. */
  get isPMI(): boolean {
    return this._compilazione?.tipoAzienda === 'PMI';
  }

  /**
   * Stringa paginatore nel formato `"N / TOT"` da mostrare nell'intestazione
   * dello swiper (es. `"3 / 12"`).
   */
  get swiperPaginator(): string {
    return `${this.currentSlideIndex + 1} / ${this.totTematiche}`;
  }

  // ─── Dipendenze ───────────────────────────────────────────────────────────

  private readonly utenteIncaricatoService = inject(UtenteIncaricatoService);
  private readonly destroyRef = inject(DestroyRef);

  /** Subject throttled per il ricalcolo dell'altezza degli Swiper al resize delle textarea. */
  private readonly resizeSubject = new Subject<void>();

  /** Lista dei ResizeObserver attivi sulle textarea, da pulire nel destroy. */
  private observers: ResizeObserver[] = [];

  constructor(
    private readonly utilityService: UtilityService,
    private readonly unitaDiMisuraService: UnitaDiMisuraService,
    private readonly reportVsmeService: ReportVsmeService,
    private readonly cdRef: ChangeDetectorRef,
    private readonly aziendaService: AziendaService,
    public readonly dialog: MatDialog,
    private readonly _bottomSheet: MatBottomSheet,
  ) {
    this._initResizeSwiperOnTextAreaChange();
  }

  // ─── Lifecycle ────────────────────────────────────────────────────────────

  /** @inheritdoc */
  ngAfterViewInit(): void {
    this._listaFigureAz();
    this._initSwiper();
    this.cdRef.detectChanges();
  }

  /** @inheritdoc */
  ngOnChanges(changes: SimpleChanges): void {
    const changeCompilazione = changes['compilazione'];
    const isCambiato = changeCompilazione?.currentValue && !changeCompilazione.firstChange;

    if (isCambiato) {
      setTimeout(() => this.moveToTematicaSenzaRisposte(), 0);
    }
  }

  /** @inheritdoc */
  ngOnDestroy(): void {
    this._cleanupObservers();
  }

  // ─── Navigazione Swiper ───────────────────────────────────────────────────

  /**
   * Gestisce il passaggio alla slide successiva oppure la pubblicazione finale.
   *
   * Flusso:
   * 1. In sola lettura → avanza senza validare.
   * 2. Verifica che almeno una sotto-opzione per risposta sia selezionata.
   * 3. Esegue il salvataggio e avanza.
   *
   * @returns `false` se la validazione fallisce o l'utente annulla, `void` altrimenti.
   */
  async avanti(): Promise<boolean | void> {
    if (this.readOnly) {
      return this.avantiSlide();
    }

    if (!this.ctrElementoCheckato()) {
      return false;
    }

    return this._eseguiSalvataggio();
  }

  /**
   * Gestisce il ritorno alla slide precedente.
   * Se siamo alla prima slide assoluta, emette l'evento `completato`.
   */
  indietro(): void {
    if (!this.ctrElementoCheckato()) return;

    if (this.currentSlideIndex === 0 && !this.readOnly) {
      this.completato.emit(true);
      return;
    }

    const currentAmbitoIndex = this.swiperAmbiti.nativeElement.swiper.realIndex;
    const swiperFiglio = this.swiperTematiche.get(currentAmbitoIndex)?.nativeElement.swiper;
    const isFirstTematica = (swiperFiglio?.realIndex ?? 0) === 0;

    if (isFirstTematica) {
      this.swiperAmbiti.nativeElement.swiper.slidePrev();
      this._posizionaUltimaSlideAmbitoPrecedente();
    } else {
      swiperFiglio?.slidePrev();
    }
  }

  /**
   * Avanza allo slide successivo navigando tra tematiche e ambiti in sequenza.
   * Se siamo all'ultima slide assoluta e non siamo in sola lettura, emette `completato`.
   */
  avantiSlide(): void {
    if (this.currentSlideIndex + 1 === this.totTematiche && !this.readOnly) {
      this.completato.emit(true);
      return;
    }

    const currentAmbitoIndex = this.swiperAmbiti.nativeElement.swiper.realIndex;
    const swiperFiglio = this.swiperTematiche.get(currentAmbitoIndex)?.nativeElement.swiper;
    const totTematicheInAmbito = this.ambiti[currentAmbitoIndex]?.tematiche.length ?? 0;
    const isLastTematica = (swiperFiglio?.realIndex ?? 0) + 1 === totTematicheInAmbito;

    if (isLastTematica) {
      this.swiperAmbiti.nativeElement.swiper.slideNext();
    } else {
      swiperFiglio?.slideNext();
    }
  }

  /**
   * Callback invocata al cambio di slide degli Swiper.
   * Sincronizza gli indici locali e forza l'aggiornamento dell'altezza.
   */
  onSlideChange(): void {
    this.indexAmbitoCorrente = this.swiperAmbiti.nativeElement.swiper.realIndex;
    this.indexTematicaCorrente =
      this.swiperTematiche.get(this.indexAmbitoCorrente)?.nativeElement.swiper.realIndex ?? 0;

    const slidePassate = this.ambiti
      .slice(0, this.indexAmbitoCorrente)
      .reduce((acc, amb) => acc + amb.tematiche.length, 0);

    this.currentSlideIndex = slidePassate + this.indexTematicaCorrente;
    this._updateSwiperHeight();
  }

  /**
   * Cerca la prima tematica senza risposte complete e sposta gli Swiper su di essa.
   *
   * @returns `true` se è stata trovata una tematica incompleta e lo swiper è stato spostato,
   *          `false` se tutte le tematiche sono complete.
   */
  public moveToTematicaSenzaRisposte(): boolean {
    const target = this._findTematicaSenzaRisposte();

    if (target.indiceAmbito === undefined) return false;

    this.currentSlideIndex = target.currentSlideIndex;

    const swiperPadre = this.swiperAmbiti?.nativeElement?.swiper;
    swiperPadre?.slideTo(target.indiceAmbito);

    const swiperFiglioRef = this.swiperTematiche.get(target.indiceAmbito);
    if (swiperFiglioRef) {
      const swiperFiglio = swiperFiglioRef.nativeElement.swiper;
      swiperFiglio.slideTo(target.indiceTematica ?? 0);
      this.indexTematicaCorrente = swiperFiglio.realIndex;
    }

    return true;
  }

  // ─── Gestione risposte ────────────────────────────────────────────────────

  /**
   * Aggiorna la lista delle risposte selezionate per una tematica
   * quando l'utente spunta/togliere il segno di spunta su un'opzione.
   *
   * @param event  Evento del checkbox (con proprietà `checked`).
   * @param tematica  La tematica a cui appartiene l'opzione.
   * @param opz  L'opzione selezionata/deselezionata (deve avere una proprietà `chiave`).
   */
  cambioCheckOpzione(
    event: { checked: boolean },
    tematica: Tematica,
    opz: { chiave: string },
  ): void {
    tematica.risposte ??= [];

    if (event.checked) {
      if (!tematica.risposte.includes(opz.chiave)) {
        tematica.risposte.push(opz.chiave);
      }
    } else {
      tematica.risposte = tematica.risposte.filter((r) => r !== opz.chiave);
    }

    this._updateSwiperHeight();
  }

  /**
   * Aggiorna lo stato `checked` di una sotto-opzione e forza l'aggiornamento
   * dell'altezza degli Swiper.
   *
   * @param event    Evento del checkbox.
   * @param sottoOpz La sotto-opzione da aggiornare.
   */
  sottoOpzAzioneKpiChecked(event: { checked: boolean }, sottoOpz: OpzioneTematica): void {
    this.cambioCheckSottoOpzione(event, sottoOpz);
    this._updateSwiperHeight();
  }

  /**
   * Aggiorna direttamente la proprietà `checked` di una sotto-opzione.
   *
   * @param event    Evento del checkbox con la nuova proprietà `checked`.
   * @param sottoOpz La sotto-opzione da aggiornare.
   */
  cambioCheckSottoOpzione(event: { checked: boolean }, sottoOpz: any): void {
    sottoOpz.checked = event.checked;
  }

  /**
   * Verifica se una chiave è presente nell'elenco delle sotto-risposte selezionate.
   *
   * @param sottoRisposte Array delle chiavi selezionate.
   * @param chiave        Chiave da ricercare.
   * @returns `true` se la chiave è presente, `false` altrimenti.
   */
  checkSotto(sottoRisposte: string[] | undefined, chiave: string): boolean {
    return sottoRisposte?.includes(chiave) ?? false;
  }

  /**
   * Callback invocata quando la motivazione di una risposta cambia.
   * Aggiorna l'altezza degli Swiper solo se la variazione è effettiva.
   *
   * @param changed `true` se la motivazione è cambiata.
   */
  motivazioneChanged(changed: boolean): void {
    if (changed) {
      this._updateSwiperHeight();
    }
  }

  // ─── Tabella dinamica ─────────────────────────────────────────────────────

  /**
   * Costruisce l'oggetto `TabellaDinamica` da passare al componente
   * `app-tabella-cfg-dinamica` a partire da una sotto-opzione.
   *
   * @param sottoOpz La sotto-opzione contenente i dati della tabella.
   * @returns L'oggetto `TabellaDinamica` pronto per il rendering.
   */
  generaOggettoTabella(sottoOpz: OpzioneTematica): TabellaDinamica {
    return {
      colonne: sottoOpz.colonne,
      id: sottoOpz.id,
      righe: sottoOpz.righe,
      totaleColonne: sottoOpz.totaleColonne,
      totaleRighe: sottoOpz.totaleRighe,
      titoloTabella: sottoOpz.titoloTabella,
    };
  }

  /**
   * Aggiorna la sotto-opzione con i dati della tabella modificata dall'utente.
   *
   * @param tabellaAggiornata La tabella con i nuovi valori.
   * @param sottoOpz          La sotto-opzione da aggiornare.
   */
  aggiornaTabella(tabellaAggiornata: TabellaDinamica, sottoOpz: OpzioneTematica): void {
    sottoOpz.colonne = tabellaAggiornata.colonne;
    sottoOpz.righe = tabellaAggiornata.righe;
    sottoOpz.totaleColonne = tabellaAggiornata.totaleColonne;
    sottoOpz.totaleRighe = tabellaAggiornata.totaleRighe;
  }

  /**
   * Forza il ricalcolo dell'altezza degli Swiper quando le celle della tabella
   * cambiano dimensione.
   *
   * @param variazioneDimensioneCelle `true` se la dimensione delle celle è cambiata.
   */
  renderizzaSwiper(variazioneDimensioneCelle: boolean): void {
    if (variazioneDimensioneCelle) {
      this._updateSwiperHeight();
    }
  }

  // ─── Figure aziendali ─────────────────────────────────────────────────────

  /**
   * Restituisce il testo del tooltip per una figura aziendale.
   * Se la figura è definita, mostra `"<ruolo> : <nome>"`;
   * altrimenti mostra il referente dell'azienda.
   *
   * @param figura Oggetto figura con le proprietà `figura` e `nome`.
   * @returns Il testo da visualizzare nel tooltip.
   */
  toolTipFigura(figura: { figura: string; nome: string } | undefined): string {
    if (figura) {
      return `${figura.figura} : ${figura.nome}`;
    }
    return this.referente;
  }

  /**
   * Apre il dialog di associazione della figura aziendale a una sotto-risposta.
   * Prima del dialog viene eseguito un salvataggio automatico per evitare perdita di dati.
   *
   * @param idAmbito        ID dell'ambito di appartenenza.
   * @param idTematica      ID della tematica di appartenenza.
   * @param chiaveRisposta  Chiave della risposta selezionata.
   * @param sottoRisposta   Oggetto della sotto-risposta a cui associare la figura.
   * @param figura          Figura attualmente associata (o `null`).
   */
  async associaFiguraAziendale(
    idAmbito: string,
    idTematica: string,
    chiaveRisposta: string,
    sottoRisposta: any,
    figura: any,
  ): Promise<void> {
    const isSaved = await this.salvaCompilazione();
    if (!isSaved) return;

    const dialogRef = this.dialog.open(DialogAssociaFiguraAziendaleComponent, {
      data: { figura },
      panelClass: 'dialog-container',
      disableClose: false,
      width: '50%',
      maxHeight: '95%',
      autoFocus: false,
    });

    dialogRef.afterClosed().subscribe((idFigura: string | undefined) => {
      if (idFigura) {
        this._salvaFigAz(idAmbito, idTematica, chiaveRisposta, sottoRisposta, idFigura);
      }
    });
  }

  // ─── Validazione ──────────────────────────────────────────────────────────

  /**
   * Verifica che almeno una risposta VSME sia selezionata globalmente
   * e che ogni risposta selezionata abbia almeno una sotto-opzione spuntata.
   *
   * Mostra una snack bar di errore se il controllo fallisce.
   *
   * @returns `true` se la validazione è superata, `false` altrimenti.
   */
  public ctrElementoCheckato(): boolean {
    if (!this._compilazione?.ambiti) return true;

    const haAlmenoUnaRisposta = this._compilazione.ambiti.some((amb) =>
      amb.tematiche.some((tema) =>
        tema.risposteVsme.some((risp) => tema.risposte?.includes(risp.chiave)),
      ),
    );

    if (!haAlmenoUnaRisposta) {
      this._showSnack('Seleziona almeno una risposta', SNACK_DURATION_SHORT);
      return false;
    }

    for (const amb of this._compilazione.ambiti) {
      for (const tema of amb.tematiche) {
        if (!this.isSottoOpzioneChecked(tema)) return false;
      }
    }

    return true;
  }

  /**
   * Verifica che ogni risposta selezionata nella tematica abbia almeno
   * una sotto-opzione spuntata. Mostra un errore in caso contrario.
   *
   * @param tematica La tematica da validare.
   * @returns `true` se tutte le risposte selezionate hanno almeno una sotto-opzione, `false` altrimenti.
   */
  isSottoOpzioneChecked(tematica: Tematica): boolean {
    const risposteChecked = tematica.risposteVsme.filter((risp) =>
      tematica.risposte?.includes(risp.chiave),
    );

    const tutteHannoSotto = risposteChecked.every((risp) =>
      risp.sottoOpzioni?.some((sotto) => sotto.checked),
    );

    if (!tutteHannoSotto) {
      this._showSnack(
        'Ogni risposta selezionata deve avere almeno una sotto-opzione selezionata',
        SNACK_DURATION_SHORT,
      );
      return false;
    }

    return true;
  }

  /**
   * Valida la compilazione prima della pubblicazione finale.
   *
   * Controlla:
   * 1. Che la tematica corrente abbia almeno una risposta selezionata.
   * 2. Che non esistano tematiche senza risposte complete (tramite `moveToTematicaSenzaRisposte`).
   *
   * @returns `true` se la validazione è superata, `false` altrimenti.
   */
  async validaCompilazione(): Promise<boolean> {
    if (this.readOnly) return true;

    const ambitoCorrente = this.ambiti[this.indexAmbitoCorrente];
    const tematicaCorrente = ambitoCorrente?.tematiche[this.indexTematicaCorrente];

    if (!tematicaCorrente?.risposte?.length) {
      this._showSnack(
        'Seleziona almeno una informativa VSME per ogni tematica. Se si vuole escludere una tematica dal report, rimuoverla nello step 3: Tematiche e procedere.',
        SNACK_DURATION_LONG,
      );
      this.spinnerOver.hide();
      return false;
    }

    if (this.moveToTematicaSenzaRisposte()) {
      this._showSnack(
        'Seleziona almeno una informativa VSME per ogni tematica. Se si vuole escludere una tematica dal report, rimuoverla nello step 3: Tematiche e procedere.',
        SNACK_DURATION_LONG,
      );
      this.spinnerOver.hide();
      return false;
    }

    return true;
  }

  // ─── Salvataggio ──────────────────────────────────────────────────────────

  /**
   * Invia la compilazione corrente al backend tramite `ReportVsmeService`.
   *
   * @returns `true` se il salvataggio è andato a buon fine, `false` in caso di errore.
   */
  public async salvaCompilazione(): Promise<boolean> {
    this.spinnerOver.show();

    try {
      const result = await firstValueFrom(
        this.reportVsmeService.salvaCompilazione(this._idModulo, this.ambiti),
      );
      return !!result;
    } catch {
      this.utilityService.openDialog({
        titolo: 'Attenzione',
        descrizione: 'Errore nel salvataggio del questionario',
        bottoni: [{ nome_btn: 'Ok' }],
      });
      return false;
    } finally {
      this.spinnerOver.hide();
    }
  }

  // ─── Tooltip e AI ─────────────────────────────────────────────────────────

  /**
   * Restituisce il testo del tooltip per un campo pre-compilato da un documento precedente.
   *
   * @param preCompilazioneInfos Stringa nel formato `"<CODICE>_<ANNO>"`.
   * @returns Il testo del tooltip localizzato in italiano, oppure stringa vuota.
   */
  getTooltip(preCompilazioneInfos?: string): string {
    if (!preCompilazioneInfos) return '';

    const [codice, anno] = preCompilazioneInfos.split('_') as [CodicePreCompilazione, string];

    const tooltipMap: Record<CodicePreCompilazione, string> = {
      PA: `Risposta proposta dalla Policy di Sostenibilità dell'anno ${anno} precedentemente associata`,
      SA: `Risposta proposta dalla Sintesi di Sostenibilità dell'anno ${anno} precedentemente associata`,
      AA: `Risposta proposta dall'Assessment dell'anno ${anno} precedentemente associato`,
      BA: `Risposta proposta dal Report di Sostenibilità dell'anno ${anno} precedentemente associato`,
      BANN: `Risposta proposta dal Report di Sostenibilità dell'anno ${anno}`,
      SANN: `Risposta proposta dalla Sintesi di Sostenibilità dell'anno ${anno}`,
      AANN: `Risposta proposta dall'Assessment dell'anno ${anno}`,
    };

    return tooltipMap[codice] ?? '';
  }

  /**
   * Apre il bottom sheet con le informazioni di pre-compilazione
   * per un'azione o un KPI.
   *
   * @param opzioneTematica L'opzione di cui mostrare le informazioni.
   * @param tipo            Tipo dell'elemento: `'kpi'` o `'azione'`.
   */
  apriPopUpConsigliato(opzioneTematica: OpzioneTematica, tipo: 'kpi' | 'azione'): void {
    const isKpi = tipo === 'kpi';
    const info = isKpi
      ? opzioneTematica.valoriKpi?.[0]?.preCompilazioneInfos
      : opzioneTematica.preCompilazioneInfos;
    const titolo = isKpi ? opzioneTematica.nomeBreve : opzioneTematica.titolo;

    const infoElementoObj: InfoElemento = {
      infos: this.tooltipInfo(info ? [info] : []),
      titoloElemento: titolo,
      obbligatorio: false,
    };

    this._bottomSheet.open(BottomSheetInfoElementiComponent, { data: infoElementoObj });
  }

  /**
   * Converte una lista di codici di pre-compilazione nei rispettivi
   * testi descrittivi da mostrare nei tooltip informativi.
   *
   * @param info Array di stringhe nel formato `"<CODICE>_<ANNO>"`.
   * @returns Array di stringhe descrittive in italiano.
   */
  tooltipInfo(info?: string[]): string[] {
    if (!info?.length) return [];

    const labelMap: Record<string, string> = {
      MA: 'Analisi di Materialità',
      PA: 'Policy di Sostenibilità',
      SA: 'Sintesi di Sostenibilità',
      AA: 'Assessment di Sostenibilità',
      PI: 'Piano di Sostenibilità',
      BA: 'Report di Sostenibilità',
      DM: 'Doppia Materialità',
    };

    return info.reduce<string[]>((acc, raw) => {
      const [codice, anno] = raw.split('_');
      const label = labelMap[codice];
      if (label) acc.push(`${label} [${anno}]`);
      return acc;
    }, []);
  }

  /**
   * Mappa il tipo di sotto-risposta VSME nel corrispondente `TipoElemento`
   * utilizzato per costruire il prompt per l'AI.
   *
   * @param tipoSottoRisposta Tipo della sotto-risposta (`'azione'`, `'kpi'`, `'checkbox'`, `'dynamic_table'`).
   * @returns Il `TipoElemento` corrispondente, oppure `undefined` se non mappato.
   */
  getTipoElemento(tipoSottoRisposta: string): TipoElemento | undefined {
    const tipoMap: Record<string, TipoElemento> = {
      azione: 'AZIONE',
      kpi: 'KPI',
      checkbox: 'SOTTORISPOSTA',
      dynamic_table: 'SOTTORISPOSTA',
    };
    return tipoMap[tipoSottoRisposta];
  }

  /**
   * Costruisce l'oggetto `ElementoForAI` da una sotto-opzione,
   * scegliendo il campo titolo appropriato in base al tipo.
   *
   * @param elemento La sotto-opzione da convertire.
   * @returns L'oggetto `ElementoForAI` pronto per il prompt.
   */
  protected getElementoForAI(elemento: OpzioneTematica): ElementoForAI {
    const titoloMap: Record<OpzioneTematica['tipo'], string> = {
      azione: elemento.titolo,
      kpi: elemento.nomeBreve,
      dynamic_table: elemento.titoloTabella,
      checkbox: elemento.titolo,
    };

    return {
      id: elemento.id ?? '',
      titolo: titoloMap[elemento.tipo] ?? '',
      descrizione: elemento.descrizione ?? '',
    };
  }

  /**
   * Costruisce l'oggetto `ElementoForAI` da una tematica,
   * concatenando `nomeBreve` e `domanda` come titolo.
   *
   * @param tematica La tematica da convertire.
   * @returns L'oggetto `ElementoForAI` pronto per il prompt.
   */
  protected getElementoForAITematica(tematica: Tematica): ElementoForAI {
    return {
      id: tematica.id ?? '',
      titolo: `${tematica.nomeBreve ?? ''}${tematica.domanda ?? ''}`,
      descrizione: tematica.descrizione ?? '',
    };
  }

  // ─── Metodi privati ───────────────────────────────────────────────────────

  /**
   * Inizializza gli Swiper al caricamento della vista.
   * In sola lettura posiziona alla prima slide,
   * altrimenti naviga alla prima tematica senza risposte.
   */
  private _initSwiper(): void {
    setTimeout(() => {
      if (this.readOnly) {
        this.swiperAmbiti.nativeElement.swiper.slideTo(0);
      } else {
        this.moveToTematicaSenzaRisposte();
      }
    }, 0);

    this._getUnitaDiMisuraPerKpi();
  }

  /**
   * Ricalcola `totTematiche` sommando il numero di tematiche di tutti gli ambiti.
   */
  private _ricalcolaTotTematiche(): void {
    this.totTematiche = this.ambiti.reduce((acc, amb) => acc + amb.tematiche.length, 0);
  }

  /**
   * Cerca la prima tematica senza risposte complete nell'intera struttura di ambiti.
   *
   * Criteri di incompletezza:
   * - Nessuna risposta selezionata (`risposte` vuoto o assente).
   * - Altrimenti: nessuna sotto-opzione selezionata per le risposte scelte.
   *
   * @returns Un oggetto `TematicaTarget` con gli indici per la navigazione.
   */
  private _findTematicaSenzaRisposte(): TematicaTarget {
    if (this._compilazione?.stato === 'PUBBLICATO') {
      return { indiceAmbito: undefined, indiceTematica: undefined, currentSlideIndex: 0 };
    }

    let currentSlideIndex = 0;

    for (let idxAmb = 0; idxAmb < this.ambiti.length; idxAmb++) {
      const tematiche = this.ambiti[idxAmb].tematiche ?? [];

      const idxIncompleto = tematiche.findIndex((tema) => {
        const haRisposte = !!tema.risposte?.length;
        if (!haRisposte) return true;

        return !this.isSottoOpzioneChecked(tema);
      });

      if (idxIncompleto !== -1) {
        return {
          indiceAmbito: idxAmb,
          indiceTematica: idxIncompleto,
          currentSlideIndex: currentSlideIndex + idxIncompleto,
        };
      }

      currentSlideIndex += tematiche.length;
    }

    return { indiceAmbito: undefined, indiceTematica: undefined, currentSlideIndex: 0 };
  }

  /**
   * Recupera e carica le Unità di Misura associate a tutti i KPI
   * presenti nella struttura `ambiti`, evitando chiamate duplicate.
   */
  private _getUnitaDiMisuraPerKpi(): void {
    const categorieIds = this._extractCategorieKpiIds();
    if (!categorieIds.length) return;

    this.spinnerOver.show();

    forkJoin(categorieIds.map((id) => this._loadUnitaDiMisura(id)))
      .pipe(finalize(() => this.spinnerOver.hide()))
      .subscribe();
  }

  /**
   * Estrae gli ID univoci delle categorie di Unità di Misura
   * relative alle sotto-opzioni di tipo `'kpi'` non ancora caricate.
   *
   * @returns Array di ID categoria da caricare.
   */
  private _extractCategorieKpiIds(): string[] {
    const ids = this.ambiti
      .flatMap((a) => a.tematiche ?? [])
      .flatMap((t) => t.risposteVsme ?? [])
      .flatMap((r) => r.sottoOpzioni ?? [])
      .filter((s) => s.tipo === 'kpi' && !!s.idCategoriaUnitaDiMisura)
      .map((s) => s.idCategoriaUnitaDiMisura);

    return [...new Set(ids)].filter((id) => !this.arrayUm[id]);
  }

  /**
   * Effettua la chiamata HTTP per recuperare le Unità di Misura
   * di una categoria specifica e popola `arrayUm`.
   *
   * Pre-inizializza `arrayUm[idCategoria]` a `[]` per prevenire chiamate duplicate.
   *
   * @param idCategoria ID della categoria di Unità di Misura.
   * @returns Observable completato al termine della chiamata.
   */
  private _loadUnitaDiMisura(idCategoria: string) {
    this.arrayUm[idCategoria] = [];

    const filters: Filters[] = [
      { chiave: 'idCategoriaUnitaDiMisura', operator: 'eq', values: [idCategoria] },
    ];

    const request$ = this._temporaryToken
      ? this.unitaDiMisuraService.getUnitaDiMisuraConTempToken(
          this._temporaryToken,
          0,
          1000,
          '',
          filters,
        )
      : this.unitaDiMisuraService.getUnitaDiMisura(0, 1000, '', filters);

    return request$.pipe(
      map((res) => {
        this.arrayUm[idCategoria] = res?.content ?? [];
      }),
    );
  }

  /**
   * Carica la lista delle figure aziendali per l'azienda corrente
   * e popola `referente` con la figura di tipo "referente".
   */
  private _listaFigureAz(): void {
    const idAzienda = this._compilazione?.idAzienda;
    if (!idAzienda) return;

    this.spinnerOver.show();
    this.aziendaService
      .getFigureAz(idAzienda, 0, 1000)
      .pipe(finalize(() => this.spinnerOver.hide()))
      .subscribe({
        next: (esito) => {
          this.referente = esito.content
            .filter((item: any) => item.figura.toLowerCase() === 'referente')
            .map((item: any) => `${item.figura} : ${item.nome}`)
            .join(', ');
        },
        error: (err) => console.error('[CompilaReportVsme] Errore caricamento figure:', err),
      });
  }

  /**
   * Chiama il servizio per associare una figura aziendale a una sotto-risposta
   * e aggiorna l'oggetto locale con il risultato restituito dal backend.
   *
   * @param idAmbito       ID dell'ambito.
   * @param idTematica     ID della tematica.
   * @param chiaveRisposta Chiave della risposta.
   * @param sottoRisposta  Sotto-risposta da aggiornare.
   * @param idFigAziendale ID della figura aziendale selezionata.
   */
  private _salvaFigAz(
    idAmbito: string,
    idTematica: string,
    chiaveRisposta: string,
    sottoRisposta: any,
    idFigAziendale: string,
  ): void {
    this.spinnerOver.show();

    this.reportVsmeService
      .putAssegnaFiguraElementoVsme(
        this._idModulo,
        idAmbito,
        idTematica,
        chiaveRisposta,
        sottoRisposta.id,
        idFigAziendale,
      )
      .pipe(finalize(() => this.spinnerOver.hide()))
      .subscribe({
        next: (esito) => Object.assign(sottoRisposta, esito),
        error: (err) =>
          console.error('[CompilaReportVsme] Errore salvataggio figura aziendale:', err),
      });
  }

  /**
   * Mostra il dialog di conferma prima della pubblicazione finale.
   *
   * @returns `true` se l'utente ha confermato, `false` se ha annullato.
   */
  private async _confermaPubblicazione(): Promise<boolean> {
    const dialog = this.utilityService.openDialog({
      titolo: 'Confermare dati inseriti?',
      descrizione: 'Non sarà più possibile modificarli',
      bottoni: [
        { nome_btn: 'No', id_btn: 'N' },
        { nome_btn: 'Si', id_btn: 'S' },
      ],
    });

    const scelta = await firstValueFrom(dialog.beforeClosed());
    return scelta === 'S';
  }

  /**
   * Esegue il salvataggio della compilazione e, se riuscito, avanza alla slide successiva.
   *
   * @returns `false` se il salvataggio fallisce, `void` altrimenti.
   */
  private async _eseguiSalvataggio(): Promise<boolean | void> {
    const isSaved = await this.salvaCompilazione();

    if (!isSaved) {
      console.warn('[CompilaReportVsme] Salvataggio fallito.');
      return false;
    }

    this.avantiSlide();
  }

  /**
   * Posiziona lo Swiper figlio dell'ambito precedente all'ultima slide.
   * Invocato dopo `slidePrev()` sullo Swiper padre degli ambiti.
   */
  private _posizionaUltimaSlideAmbitoPrecedente(): void {
    const newIndex = this.swiperAmbiti.nativeElement.swiper.realIndex;
    const prevSwiper = this.swiperTematiche.get(newIndex)?.nativeElement.swiper;

    if (prevSwiper) {
      prevSwiper.slideTo(prevSwiper.slides.length - 1);
    }
  }

  /**
   * Pianifica l'aggiornamento dell'altezza degli Swiper con un delay
   * per permettere al DOM di aggiornarsi prima del ricalcolo.
   */
  private _updateSwiperHeight(): void {
    setTimeout(() => {
      this.swiperTematiche?.forEach((swiper) => {
        swiper.nativeElement.swiper.update();
      });
      this.swiperAmbiti?.nativeElement.swiper.update();
    }, SWIPER_UPDATE_DELAY_MS);
  }

  /**
   * Forza l'aggiornamento del layout di tutti gli Swiper al prossimo frame,
   * garantendo che il DOM sia completamente aggiornato prima del resize.
   */
  private _forceSwiperUpdate(): void {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.swiperTematiche?.forEach((s) => s.nativeElement.swiper?.update());
        this.swiperAmbiti?.nativeElement.swiper?.update();
      });
    });
  }

  /**
   * Inizializza il sistema reattivo per aggiornare l'altezza degli Swiper
   * quando le `<textarea>` vengono ridimensionate dall'utente.
   *
   * - Sottoscrive `resizeSubject` con throttle `auditTime(SWIPER_RESIZE_AUDIT_MS)`.
   * - Usa `DestroyRef` per pulire automaticamente la sottoscrizione.
   * - Usa un `effect` di Angular 19 per osservare le textarea correnti
   *   e ricrea i `ResizeObserver` ad ogni cambiamento della lista.
   */
  private _initResizeSwiperOnTextAreaChange(): void {
    const subscription = this.resizeSubject
      .pipe(auditTime(SWIPER_RESIZE_AUDIT_MS))
      .subscribe(() => this._forceSwiperUpdate());

    this.destroyRef.onDestroy(() => subscription.unsubscribe());

    effect(() => {
      this._cleanupObservers();
      this.textAreas().forEach((ref) => {
        const ro = new ResizeObserver(() => this.resizeSubject.next());
        ro.observe(ref.nativeElement);
        this.observers.push(ro);
      });
    });
  }

  /**
   * Scollega e rimuove tutti i `ResizeObserver` attivi per evitare memory leak.
   */
  private _cleanupObservers(): void {
    this.observers.forEach((ro) => ro.disconnect());
    this.observers = [];
  }

  /**
   * Esegue la pre-selezione iniziale per i moduli di tipo PMI:
   * spunta automaticamente tutte le risposte e sotto-opzioni di tipo "modulo base"
   * (`moduloCompleto === false`).
   */
  private mapPrecheckIniziale(): void {
    this._compilazione?.ambiti.forEach((amb) => {
      amb.tematiche.forEach((tematica) => {
        tematica.risposte ??= [];

        tematica.risposteVsme.forEach((rispVsme) => {
          if (!rispVsme.moduloCompleto) {
            this._assicuraRispostaPresente(tematica, rispVsme.chiave);
            this._settaSottoOpzioniObbligatorie(rispVsme);
          }
        });
      });
    });
  }

  /**
   * Aggiunge la chiave della risposta alla lista `tematica.risposte`
   * se non è già presente, mantenendo l'immutabilità dell'array.
   *
   * @param tematica La tematica da aggiornare.
   * @param chiave   La chiave della risposta da aggiungere.
   */
  private _assicuraRispostaPresente(tematica: Tematica, chiave: string): void {
    if (!tematica.risposte!.includes(chiave)) {
      tematica.risposte = [...tematica.risposte!, chiave];
    }
  }

  /**
   * Imposta `checked = true` su tutte le sotto-opzioni di tipo "modulo base"
   * (`moduloCompleto === false`) di una risposta VSME.
   *
   * @param rispVsme La risposta VSME di cui pre-selezionare le sotto-opzioni.
   */
  private _settaSottoOpzioniObbligatorie(rispVsme: OpzioneTematica): void {
    rispVsme.sottoOpzioni?.forEach((sotto) => {
      if (!sotto.moduloCompleto) sotto.checked = true;
    });
  }

  /**
   * Mostra una snack bar di errore con messaggio e durata configurabili.
   *
   * @param messaggio Testo da visualizzare nella snack bar.
   * @param durata    Durata in millisecondi.
   */
  private _showSnack(messaggio: string, durata: number): void {
    this.utilityService.openSnackBar(messaggio, '', {
      duration: durata,
      panelClass: ['red-snackbar', 'mrg-bottom-70'],
    });
  }
}
